import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export type TomVendingInferredSaleRow = {
  id: number;
  prevSiteVisitId: number | null;
  curSiteVisitId: number;
  locationTomVendingId: number;
  locationName: string;
  vendingMachineId: number;
  assetId: string | null;
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  periodStartDate: string | null;
  periodEndDate: string;
  prevEndingQty: number | null;
  curParQty: number;
  unitsSold: number;
  unitPriceCents: number | null;
  unitCostCents: number | null;
  retailCents: number | null;
  marginCents: number | null;
  missingPrice: boolean;
  missingCost: boolean;
  priceFromHistory: boolean;
  gapDays: number | null;
};

export type TomVendingReconciliationRow = {
  siteVisitId: number;
  visitDate: string;
  locationTomVendingId: number;
  locationName: string;
  userDisplayName: string | null;
  vendingMachineId: number | null;
  assetId: string | null;
  expectedRetailCents: number;
  cashCollectedCents: number;
  varianceCents: number;
  variancePct: number | null;
  cashGranularity: 'site' | 'machine' | 'none';
  missingCash: boolean;
  missingPrices: boolean;
  inferredLineCount: number;
};

export async function queryTomVendingInferredSales(params: {
  dateFrom: string;
  dateTo: string;
  locationTomVendingId?: number;
  skip?: number;
  take?: number;
}): Promise<{
  data: TomVendingInferredSaleRow[];
  total: number;
  summary: { totalUnitsSold: number; totalRetailCents: number; rowsMissingPrice: number; rowsMissingCost: number };
}> {
  const response = await httpClient.post('api/tom-vending/reports/sales-inferred/query', {
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    locationTomVendingId: params.locationTomVendingId,
    skip: params.skip ?? 0,
    take: params.take ?? 50,
  });
  return response.data as {
    data: TomVendingInferredSaleRow[];
    total: number;
    summary: { totalUnitsSold: number; totalRetailCents: number; rowsMissingPrice: number; rowsMissingCost: number };
  };
}

export async function backfillTomVendingInferredSales(params: {
  dateFrom: string;
  dateTo: string;
}): Promise<{ visits: number; rows: number }> {
  const response = await httpClient.post('api/tom-vending/reports/sales-inferred/backfill', params);
  return response.data as { visits: number; rows: number };
}

export async function queryTomVendingReconciliation(params: {
  dateFrom: string;
  dateTo: string;
  locationTomVendingId?: number;
  skip?: number;
  take?: number;
}): Promise<{ data: TomVendingReconciliationRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/reports/reconciliation/query', {
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    locationTomVendingId: params.locationTomVendingId,
    skip: params.skip ?? 0,
    take: params.take ?? 50,
  });
  return response.data as { data: TomVendingReconciliationRow[]; total: number };
}

export async function bulkUpsertTomVendingSalePrices(params: {
  rows: Array<{ upc: string; priceCents?: number; priceDollars?: number }>;
  note?: string | null;
}): Promise<{ updated: number; skipped: number; notFoundUpcs: string[] }> {
  const response = await httpClient.post('api/tom-vending/inventory/price/bulk-upsert', params);
  return response.data as { updated: number; skipped: number; notFoundUpcs: string[] };
}

export function formatUsdFromCents(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export type TomVendingDailySummaryCashRow = {
  siteVisitId: number;
  visitDate: string;
  locationTomVendingId: number;
  locationName: string;
  vendingMachineId: number | null;
  assetId: string | null;
  billsCents: number;
  coinsCents: number;
  totalCents: number;
  notes: string | null;
  cashSource: 'snapshot' | 'pending';
};

export type TomVendingDailySummaryProductUsedRow = {
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  unitsSold: number;
  retailCents: number;
};

export type TomVendingDailySummaryFillRow = {
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  fillQty: number;
  rejectQty: number;
  recycleQty: number;
};

export type TomVendingDailySummaryHeader = {
  visitCount: number;
  locationCount: number;
  totalCashCents: number;
  totalFillQty: number;
  totalRejectQty: number;
  totalRecycleQty: number;
  totalUnitsSold: number;
  totalRetailCents: number;
};

export type TomVendingDailySummaryResult = {
  summary: TomVendingDailySummaryHeader;
  cashRows: TomVendingDailySummaryCashRow[];
  productUsed: TomVendingDailySummaryProductUsedRow[];
  fills: TomVendingDailySummaryFillRow[];
};

const EMPTY_DAILY_SUMMARY: TomVendingDailySummaryResult = {
  summary: {
    visitCount: 0,
    locationCount: 0,
    totalCashCents: 0,
    totalFillQty: 0,
    totalRejectQty: 0,
    totalRecycleQty: 0,
    totalUnitsSold: 0,
    totalRetailCents: 0,
  },
  cashRows: [],
  productUsed: [],
  fills: [],
};

function positiveLocationId(raw: unknown): number | undefined {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isInteger(n) || n <= 0) {
    return undefined;
  }
  return n;
}

export async function queryTomVendingDailySummary(params: {
  date: string;
  locationTomVendingId?: number;
}): Promise<TomVendingDailySummaryResult> {
  const locationTomVendingId = positiveLocationId(params.locationTomVendingId);
  if (locationTomVendingId == null || !/^\d{4}-\d{2}-\d{2}$/.test(params.date ?? '')) {
    return EMPTY_DAILY_SUMMARY;
  }
  const response = await httpClient.post('api/tom-vending/reports/daily-summary/query', {
    date: params.date,
    locationTomVendingId,
  });
  return response.data as TomVendingDailySummaryResult;
}

export async function listTomVendingDailySummaryDays(params: {
  locationTomVendingId?: number;
}): Promise<{ days: string[] }> {
  const locationTomVendingId = positiveLocationId(params.locationTomVendingId);
  if (locationTomVendingId == null) {
    return { days: [] };
  }
  const response = await httpClient.post('api/tom-vending/reports/daily-summary/days', {
    locationTomVendingId,
  });
  return response.data as { days: string[] };
}
